import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import * as dayjs from "dayjs";
import IntlMessages from "util/IntlMessages";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid
} from "@material-ui/core";
import FormInput from "components/Form/Controls/input";
import FormSelectAutoComplete from "components/Form/Controls/select-autocomplete";
import FormDatePicker from "components/Form/Controls/datepicker";
import ButtonAsync from "components/Form/Controls/ButtonAsync";


const PanhematinForm = ({
    rowObj,
    prescriptionId,
    openCRU,
    handleRequestClose,
    handleCreateUpdate,
    readOnly,
    boolList,
    justificationList
}) => {
    const getCboValue = (items, id) => {
        if (typeof id === "object") return id;
        const result = items.find((x) => x.id === id);
        return result ? { label: result.name, value: result.id } : "";
    };

    const defaultValues = {
        applicationStartDate: null,
        applicationEndDate: null,
        justificationNonFormulation: null,
        pendingPrescriptionDate: null
    };

    const validationSchema = yup.object().shape({
        applicationStartDate: yup.date().nullable().required(<IntlMessages id="core.fieldRequired" />),
        applicationEndDate: yup.date().nullable().required(<IntlMessages id="core.fieldRequired" />),
        justificationNonFormulation: yup.object().nullable(),
        pendingPrescriptionDate: yup.date().nullable()
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema)
    });

    const { handleSubmit, setValue, errors } = methods;

    useEffect(() => {
        if (openCRU) {
            setTimeout(() => loadData(rowObj), 150);
        }
    }, [openCRU]);

    const loadData = (row) => {
        if (!row) return;
        setValue("applicationStartDate", row.applicationStartDate ? dayjs(row.applicationStartDate) : null);
        setValue("applicationEndDate", row.applicationEndDate ? dayjs(row.applicationEndDate) : null);
        setValue("justificationNonFormulation", row.justificationNonFormulation ? getCboValue(justificationList, row.justificationNonFormulation) : null);
        setValue("pendingPrescriptionDate", row.pendingPrescriptionDate ? dayjs(row.pendingPrescriptionDate) : null);
    };


    const formatDateOrNull = (value) =>
        value && dayjs(value).isValid()
            ? dayjs(value).format("YYYY-MM-DDTHH:mm:ss")
            : null;

    const onSubmit = async (d) => {
        const obj = {
            ...rowObj,
            id: rowObj?.id ?? 0,
            prescriptionId: prescriptionId,
            justificationNonFormulation: d.justificationNonFormulation?.value ?? null,
            applicationStartDate: formatDateOrNull(d.applicationStartDate),
            applicationEndDate: formatDateOrNull(d.applicationEndDate),
            pendingPrescriptionDate: formatDateOrNull(d.pendingPrescriptionDate)
        };
        await handleCreateUpdate(obj);
    };

    const disabled = !!readOnly;

    return (
        <Dialog fullWidth maxWidth="md" open={openCRU} onClose={handleRequestClose}>
            <DialogTitle>
                <IntlMessages id={!rowObj ? "core.add" : "core.edit"} />
            </DialogTitle>
            <DialogContent>
                <FormProvider {...methods}>
                    <form noValidate autoComplete="off">
                        <Grid
                            container
                            spacing={2}
                            sx={{ '& .MuiFormControl-root': { width: '100%' }, '& .MuiInputBase-root': { height: 40 } }}
                        >
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="applicationStartDate"
                                    label={<IntlMessages id="panhematin.startdate" />}
                                    format="YYYY-MM-DD"
                                    required
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="applicationEndDate"
                                    label={<IntlMessages id="panhematin.enddate" />}
                                    format="YYYY-MM-DD"
                                    required
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormSelectAutoComplete
                                    name="justificationNonFormulation"
                                    label={<IntlMessages id="panhematin.justificationNonFormulation" />}
                                    options={justificationList}
                                    disabled={disabled}
                                    errorobj={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="pendingPrescriptionDate"
                                    label={<IntlMessages id="panhematin.pendingdate" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
                {!disabled && (
                    <div className="text-center mt-4">
                        <ButtonAsync
                            onClick={handleSubmit(onSubmit)}
                            variant="contained"
                            color="primary"
                        >
                            <IntlMessages id="core.save" />
                        </ButtonAsync>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleRequestClose} color="secondary">
                    <IntlMessages id="core.close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PanhematinForm;
